import React, { useState, useEffect } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Form, FormGroup, Label, Input, Row, Col,
} from "reactstrap";
import { toast } from "react-toastify";
import { vendorInvoiceAPI } from "../../../helpers/api/vendorInvoice";
import { http } from "../../../helpers/api/http";

const fetchPhaseBlocks = (phaseId) =>
  http.get(`/phases/${phaseId}/`).then((res) => res.blocks || []);

const STATUS_OPTIONS = [
  { value: "saved",   label: "Saved" },
  { value: "paid",    label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const OPTIONAL_LABEL = (
  <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>
    {" "}(optional)
  </span>
);

const FIELD_LABEL       = "fw-semibold small text-muted text-uppercase";
const FIELD_LABEL_STYLE = { letterSpacing: "0.05em", fontSize: "0.65rem" };

const EMPTY_FORM = {
  name: "", document: "", cif: "", issue_date: "", due_date: "",
  phase: "", category: "", sub_category: "", block: "",
  amount_without_tax: "", tax_amount: "", total_amount: "",
  currency: "RON", status: "saved", observation: "",
};

const REQUIRED_FIELDS = ["name", "document", "cif", "issue_date", "amount_without_tax"];
const FIELD_LABELS = {
  name: "Vendor Name", document: "Document", cif: "CIF",
  issue_date: "Issue Date", due_date: "Due Date", amount_without_tax: "Amount (excl. tax)",
};

const validate = (form) => {
  const errs = {};
  REQUIRED_FIELDS.forEach((f) => {
    if (!form[f] && form[f] !== 0) errs[f] = `${FIELD_LABELS[f]} is required`;
  });
  return errs;
};

const EditInvoiceModal = ({ open, invoice, phases, onClose, onSaved }) => {
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [errors,        setErrors]        = useState({});
  const [categories,    setCategories]    = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [blocks,        setBlocks]        = useState([]);
  const [isSaving,      setIsSaving]      = useState(false);

  const isEditMode = Boolean(invoice);

  useEffect(() => {
    if (invoice) {
      setForm({
        name:               invoice.name               || "",
        document:           invoice.document           || "",
        cif:                invoice.cif                || "",
        issue_date:         invoice.issue_date         || "",
        due_date:           invoice.due_date           || "",
        phase:              invoice.phase              || "",
        category:           invoice.category           || "",
        sub_category:       invoice.sub_category       || "",
        block:              invoice.block              || "",
        amount_without_tax: invoice.amount_without_tax || "",
        tax_amount:         invoice.tax_amount         || "",
        total_amount:       invoice.total_amount       || "",
        currency:           invoice.currency           || "RON",
        status:             invoice.status             || "saved",
        observation:        invoice.observation        || "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
  }, [invoice, open]);

  useEffect(() => {
    vendorInvoiceAPI.getAllCategories()
      .then((res) => setCategories(Array.isArray(res) ? res : res.results || []))
      .catch(() => {});
    vendorInvoiceAPI.getAllSubCategories()
      .then((res) => setSubCategories(Array.isArray(res) ? res : res.results || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (form.phase) {
      fetchPhaseBlocks(form.phase).then(setBlocks).catch(() => setBlocks([]));
    } else {
      setBlocks([]);
    }
  }, [form.phase]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "category" && !value) { updated.sub_category = ""; updated.block = ""; }
      if (name === "phase"    && !value) { updated.block = ""; }
      if (name === "amount_without_tax" || name === "tax_amount") {
        const base = parseFloat(name === "amount_without_tax" ? value : prev.amount_without_tax) || 0;
        const tax  = parseFloat(name === "tax_amount"         ? value : prev.tax_amount)         || 0;
        updated.total_amount = (base + tax).toFixed(2);
      }
      return updated;
    });
  };

  const buildPayload = () => ({
    name:               form.name,
    document:           form.document,
    cif:                form.cif,
    issue_date:         form.issue_date,
    due_date:           form.due_date || null,
    phase:              form.phase        ? parseInt(form.phase,        10) : null,
    category:           form.category     ? parseInt(form.category,     10) : null,
    sub_category:       form.sub_category ? parseInt(form.sub_category, 10) : null,
    block:              form.block        ? parseInt(form.block,        10) : null,
    amount_without_tax: form.amount_without_tax,
    tax_amount:         form.tax_amount  || "0",
    total_amount:       form.total_amount || form.amount_without_tax,
    currency:           form.currency,
    status:             form.status,
    observation:        form.observation,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setIsSaving(true);
    try {
      if (isEditMode) {
        await vendorInvoiceAPI.update(invoice.id, buildPayload());
        toast.success("Invoice updated successfully");
      } else {
        await vendorInvoiceAPI.create(buildPayload());
        toast.success("Invoice created successfully");
      }
      onSaved();
      onClose();
    } catch {
      toast.error(isEditMode ? "Failed to update invoice" : "Failed to create invoice");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose} style={{ borderBottom: "1px solid #eef0f4" }}>
        <span className="fw-bold" style={{ color: "#002045" }}>
          {isEditMode ? "Edit Vendor Invoice" : "Add Vendor Invoice"}
        </span>
      </ModalHeader>

      <Form onSubmit={handleSubmit}>
        <ModalBody className="px-4 py-4">
          <Row className="g-3">
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Vendor Name</Label>
                <Input name="name" value={form.name} onChange={handleChange} placeholder="Vendor name" className={errors.name ? "is-invalid" : ""} />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Document</Label>
                <Input name="document" value={form.document} onChange={handleChange} placeholder="Document number" className={errors.document ? "is-invalid" : ""} />
                {errors.document && <div className="invalid-feedback">{errors.document}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>CIF</Label>
                <Input name="cif" value={form.cif} onChange={handleChange} placeholder="Fiscal code" className={errors.cif ? "is-invalid" : ""} />
                {errors.cif && <div className="invalid-feedback">{errors.cif}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Status</Label>
                <Input type="select" name="status" value={form.status} onChange={handleChange}>
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Issue Date</Label>
                <Input type="date" name="issue_date" value={form.issue_date} onChange={handleChange} className={errors.issue_date ? "is-invalid" : ""} />
                {errors.issue_date && <div className="invalid-feedback">{errors.issue_date}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Due Date{OPTIONAL_LABEL}</Label>
                <Input type="date" name="due_date" value={form.due_date} onChange={handleChange} className={errors.due_date ? "is-invalid" : ""} />
                {errors.due_date && <div className="invalid-feedback">{errors.due_date}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Phase{OPTIONAL_LABEL}</Label>
                <Input type="select" name="phase" value={form.phase} onChange={handleChange}>
                  <option value="">— No phase —</option>
                  {phases?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Block{OPTIONAL_LABEL}</Label>
                <Input type="select" name="block" value={form.block} onChange={handleChange} disabled={!form.phase}>
                  <option value="">— No block —</option>
                  {blocks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Category{OPTIONAL_LABEL}</Label>
                <Input type="select" name="category" value={form.category} onChange={handleChange}>
                  <option value="">— No category —</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Sub-category{OPTIONAL_LABEL}</Label>
                <Input type="select" name="sub_category" value={form.sub_category} onChange={handleChange} disabled={!form.category}>
                  <option value="">— No sub-category —</option>
                  {subCategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Amount (excl. tax)</Label>
                <Input type="number" step="0.01" name="amount_without_tax" value={form.amount_without_tax} onChange={handleChange} placeholder="0.00" className={errors.amount_without_tax ? "is-invalid" : ""} />
                {errors.amount_without_tax && <div className="invalid-feedback">{errors.amount_without_tax}</div>}
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Tax Amount{OPTIONAL_LABEL}</Label>
                <Input type="number" step="0.01" name="tax_amount" value={form.tax_amount} onChange={handleChange} placeholder="0.00" />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Total Amount</Label>
                <Input type="number" step="0.01" name="total_amount" value={form.total_amount} readOnly placeholder="0.00" style={{ background: "#f8fafc", cursor: "not-allowed" }} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Currency</Label>
                <Input type="select" name="currency" value={form.currency} onChange={handleChange}>
                  <option value="RON">RON</option>
                  <option value="EUR">EUR</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup className="mb-0">
                <Label className={FIELD_LABEL} style={FIELD_LABEL_STYLE}>Observation{OPTIONAL_LABEL}</Label>
                <Input type="textarea" name="observation" value={form.observation} onChange={handleChange} rows={3} placeholder="Optional notes..." />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>

        <ModalFooter style={{ borderTop: "1px solid #eef0f4" }}>
          <Button color="light" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button type="submit" disabled={isSaving} style={{ background: "#002045", border: "none" }}>
            {isSaving ? "Saving..." : isEditMode ? "Save Changes" : "Add Invoice"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditInvoiceModal;
